// Запросы страницы согласия OAuth. Эндпоинты живут вне /api (`/oauth/authorize/*`), поэтому
// здесь свой тонкий fetch; ошибки — тот же формат правила 7 и тот же ApiError.

import { useMutation, useQuery } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { captureIdentity, IDENTITY_HEADER } from '@/api/identity'

/** Переход на адрес клиента — отдельным объектом, чтобы тест мог его подменить. */
export const navigation = {
  go: (url: string) => window.location.assign(url),
}

export type ConsentInfo = {
  client_id: string
  client_name: string
  redirect_host: string
  scope: string
}

type ErrorBody = { error?: { code?: string; message?: string; field?: string | null } }

async function call<T>(method: 'GET' | 'POST', url: string, body?: unknown): Promise<T> {
  const identity = captureIdentity()
  if (!identity.id) throw new ApiError(401, 'unauthorized', 'Подтверди вход')
  let response: Response
  try {
    response = await fetch(url, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? { Accept: 'application/json', [IDENTITY_HEADER]: identity.id } : { Accept: 'application/json', 'Content-Type': 'application/json', [IDENTITY_HEADER]: identity.id },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    throw new ApiError(0, 'network_error', 'Нет связи с сервером')
  }
  const data = (await response.json().catch(() => null)) as unknown
  if (!identity.isCurrent()) throw new ApiError(409, 'identity_changed', 'Аккаунт изменился. Подтверди вход заново.')
  if (!response.ok) {
    const err = (data as ErrorBody | null)?.error
    throw new ApiError(
      response.status,
      err?.code ?? 'http_error',
      err?.message ?? `Ошибка сервера (${response.status})`,
      err?.field ?? null,
    )
  }
  return data as T
}

export const oauthApi = {
  info: (request: string) => call<ConsentInfo>('GET', `/oauth/authorize/info?request=${encodeURIComponent(request)}`),
  decide: (request: string, approve: boolean) =>
    call<{ redirect_to: string }>('POST', '/oauth/authorize/decision', { request, approve }),
}

export function useConsentInfo(request: string | null) {
  return useQuery({
    queryKey: ['oauth', 'consent', request],
    queryFn: () => oauthApi.info(request ?? ''),
    enabled: Boolean(request),
    retry: false,
    staleTime: Infinity,
  })
}

export function useConsentDecision() {
  return useMutation({
    mutationFn: ({ request, approve }: { request: string; approve: boolean }) => oauthApi.decide(request, approve),
  })
}
